/**
 * useConflictResolve — owns the conflict-dialog state for a markdown
 * editor surface (Task 13).
 *
 * Flow:
 *   1. MarkdownEditor calls `onConflict(conflictPayload)` after a 409.
 *   2. The hook stashes the payload → dialog opens.
 *   3. User picks one of two actions:
 *
 *      a) **Overwrite** — re-issue the same save, but with the *current*
 *         on-disk mtime/hash as the expected values. This explicitly
 *         beats the optimistic lock; the user has seen the diff and
 *         decided their copy wins. The buffer content is the same draft
 *         that hit the 409 (so the dialog does NOT need the editor
 *         buffer — it just calls `saveFile` with the conflict's
 *         `diff.ours` text, which IS the user's draft).
 *
 *      b) **Reload and re-edit** — replace the editor buffer with the
 *         `diff.theirs` text and rebase the baseline so subsequent
 *         saves use the new mtime/hash. No save is issued.
 *
 *   4. Either action closes the dialog and updates the editor.
 *
 * Action logic is implemented as a pure reducer + pure async resolvers
 * so the smoke test can exercise the full state machine without
 * rendering React.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import type {
  MarkdownWriteConflict,
  MarkdownWriteError,
  MarkdownWriteResult,
  MarkdownWriteSuccess,
} from '../components/MarkdownEditor.js';

/* ── Public state machine ─────────────────────────────────────────────── */

export type ConflictPhase = 'idle' | 'open' | 'resolving' | 'error';

export interface ConflictState {
  readonly phase: ConflictPhase;
  readonly conflict: MarkdownWriteConflict | null;
  /** Last error from a failed overwrite/reload (other than another conflict). */
  readonly lastError: MarkdownWriteError | null;
  /** A second conflict can land while resolving the first (rare but real). */
  readonly secondaryConflict: MarkdownWriteConflict | null;
}

export const initialConflictState: ConflictState = {
  phase: 'idle',
  conflict: null,
  lastError: null,
  secondaryConflict: null,
};

export type ConflictAction =
  | { readonly type: 'open'; readonly conflict: MarkdownWriteConflict }
  | { readonly type: 'dismiss' }
  | { readonly type: 'begin_resolve' }
  | { readonly type: 'resolved' }
  | { readonly type: 'resolve_error'; readonly error: MarkdownWriteError }
  | { readonly type: 'resolve_conflict'; readonly conflict: MarkdownWriteConflict };

/**
 * Pure reducer — every state change in the conflict flow runs through
 * this. Exposed so the smoke test can verify the state machine without
 * spinning up React.
 */
export function conflictReducer(state: ConflictState, action: ConflictAction): ConflictState {
  switch (action.type) {
    case 'open':
      return {
        phase: 'open',
        conflict: action.conflict,
        lastError: null,
        secondaryConflict: null,
      };
    case 'dismiss':
      return initialConflictState;
    case 'begin_resolve':
      if (state.conflict === null) return state;
      return { ...state, phase: 'resolving', lastError: null, secondaryConflict: null };
    case 'resolved':
      return initialConflictState;
    case 'resolve_error':
      return { ...state, phase: 'error', lastError: action.error };
    case 'resolve_conflict':
      // The on-disk file moved again under us mid-resolution. Surface the
      // fresh conflict so the dialog re-renders with the newest theirs.
      return {
        phase: 'open',
        conflict: action.conflict,
        lastError: null,
        secondaryConflict: state.conflict,
      };
    default: {
      // Exhaustiveness check — unreachable.
      const _never: never = action;
      void _never;
      return state;
    }
  }
}

/* ── Pure resolver functions (no React) ───────────────────────────────── */

/**
 * The save call the hook delegates to. Same shape as
 * `MarkdownEditor.saveFile`.
 */
export type SaveFn = (req: {
  readonly path: string;
  readonly content: string;
  readonly expectedMtime: number;
  readonly expectedHash: string;
}) => Promise<MarkdownWriteResult>;

/**
 * Build the request payload for an "Overwrite" — re-sends the user's
 * draft (which IS `conflict.diff.ours`) with the *actual* on-disk
 * mtime+hash as the expected values, explicitly beating the lock.
 *
 * Exported pure so tests can assert no silent overwrite path exists.
 */
export function buildOverwriteRequest(conflict: MarkdownWriteConflict): {
  readonly path: string;
  readonly content: string;
  readonly expectedMtime: number;
  readonly expectedHash: string;
} {
  return {
    path: conflict.path,
    // The user's draft is exactly what they tried to save and hit 409 on.
    // The server's CONFLICT payload echoes that content back to us as
    // `diff.ours`, so we use it directly — that guarantees the dialog
    // can resolve a conflict even if the editor unmounted between the
    // failed save and the user clicking "Overwrite".
    content: conflict.diff.ours,
    expectedMtime: conflict.actualMtime,
    expectedHash: conflict.actualHash,
  };
}

/**
 * Build the payload the editor should swap into its buffer + baseline
 * when the user picks "Reload and re-edit".
 */
export function buildReloadPayload(conflict: MarkdownWriteConflict): {
  readonly content: string;
  readonly mtimeMs: number;
  readonly sha256: string;
} {
  return {
    content: conflict.diff.theirs,
    mtimeMs: conflict.actualMtime,
    sha256: conflict.actualHash,
  };
}

/* ── React hook ──────────────────────────────────────────────────────── */

export interface UseConflictResolveOptions {
  /** The save function to call on Overwrite. Same one MarkdownEditor uses. */
  readonly saveFile: SaveFn;
  /**
   * Called when the user picks "Reload and re-edit". The host (typically
   * MarkdownEditor via its imperative handle) must replace the buffer
   * AND advance the baseline to the disk version so subsequent saves
   * use the right mtime/hash.
   */
  readonly onReload: (next: { readonly content: string; readonly mtimeMs: number; readonly sha256: string }) => void;
  /** Called after a successful overwrite so the editor can update its baseline. */
  readonly onOverwritten?: (success: MarkdownWriteSuccess) => void;
}

export interface UseConflictResolveResult {
  readonly state: ConflictState;
  /** Wire this into `MarkdownEditor.onConflict`. */
  readonly openConflict: (conflict: MarkdownWriteConflict) => void;
  /** User clicked "Cancel" / pressed Escape. */
  readonly dismiss: () => void;
  /** User clicked "Overwrite". Returns the final result for tests. */
  readonly overwrite: () => Promise<MarkdownWriteResult | null>;
  /** User clicked "Reload and re-edit". Synchronous. */
  readonly reload: () => void;
}

export function useConflictResolve(opts: UseConflictResolveOptions): UseConflictResolveResult {
  const [state, setState] = useState<ConflictState>(initialConflictState);
  // Mirror state into a ref so async callbacks read the latest conflict
  // without becoming stale (the hook's identity is stable across renders).
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch = useCallback((action: ConflictAction) => {
    setState((prev) => conflictReducer(prev, action));
  }, []);

  const openConflict = useCallback(
    (conflict: MarkdownWriteConflict) => {
      dispatch({ type: 'open', conflict });
    },
    [dispatch],
  );

  const dismiss = useCallback(() => {
    dispatch({ type: 'dismiss' });
  }, [dispatch]);

  const reload = useCallback(() => {
    const conflict = stateRef.current.conflict;
    if (conflict === null) return;
    opts.onReload(buildReloadPayload(conflict));
    dispatch({ type: 'resolved' });
  }, [dispatch, opts]);

  const overwrite = useCallback(async (): Promise<MarkdownWriteResult | null> => {
    const conflict = stateRef.current.conflict;
    if (conflict === null) return null;
    dispatch({ type: 'begin_resolve' });
    const req = buildOverwriteRequest(conflict);
    const result = await opts.saveFile(req);
    if (result.ok) {
      opts.onOverwritten?.(result.value);
      dispatch({ type: 'resolved' });
      return result;
    }
    if ('conflict' in result) {
      dispatch({ type: 'resolve_conflict', conflict: result.conflict });
      return result;
    }
    dispatch({ type: 'resolve_error', error: result.error });
    return result;
  }, [dispatch, opts]);

  return useMemo(
    () => ({ state, openConflict, dismiss, overwrite, reload }),
    [state, openConflict, dismiss, overwrite, reload],
  );
}

export default useConflictResolve;
